import { faker } from "@faker-js/faker";
import { add, format } from "date-fns";

import {
  fakeAppointmentType,
  fakeBookingProviderType,
} from "@/__mocks__/mockBaseTypes";
import { merge } from "@/__mocks__/mockHelpers";
import { DateFormat } from "@/constants/DateFormat";

const fakeTimeSlot = (overrides?: DeepPartial<TimeSlot>): TimeSlot => {
  const start = faker.date.soon();
  const end = new Date(start);
  end.setHours(end.getHours() + 1);

  const timeSlot: TimeSlot = {
    end: end.toISOString().split(".")[0],
    participants: [],
    start: start.toISOString().split(".")[0],
  };

  return merge(timeSlot, overrides);
};

const fakeTimeSlots = (count?: number): TimeSlot[] => {
  const timeSlots = faker.helpers.multiple(() => fakeTimeSlot(), {
    count: count ?? faker.number.int({ max: 5, min: 1 }),
  });

  // anchor every slot to the first slot's time, offset by a whole hour per index, so each slot's displayed time (h:mm aaa) is unique.
  // this is to prevent collisions where multiple time slots have the same hour and minute component, and cause test flakiness
  return Array.from(timeSlots.entries()).map(([index, timeSlot]) => ({
    ...timeSlot,
    end: add(new Date(timeSlots[0].end), { hours: index })
      .toISOString()
      .split(".")[0],
    start: add(new Date(timeSlots[0].start), { hours: index })
      .toISOString()
      .split(".")[0],
  }));
};

const fakeTimeSlotGroup = (
  overrides?: DeepPartial<TimeSlotGroup>,
): TimeSlotGroup => {
  const date = faker.date.soon();
  const timeSlotGroup: TimeSlotGroup = {
    date: format(date, DateFormat.shortNumberMonthDayYear),
    timeSlots: fakeTimeSlots(),
  };

  return merge(timeSlotGroup, overrides);
};

const fakeDietitianProviderType = (
  overrides?: DeepPartial<BookingProviderType>,
): BookingProviderType => {
  const appointmentTypeGroup = "DIETITIAN";
  const providerType = fakeBookingProviderType({
    appointmentTypes: [
      fakeAppointmentType({ appointmentTypeGroup, durationInMinutes: 25 }),
      fakeAppointmentType({ appointmentTypeGroup, durationInMinutes: 50 }),
    ],
    providerType: "Dietitian",
  });

  return merge(providerType, overrides);
};

export { fakeDietitianProviderType, fakeTimeSlot, fakeTimeSlotGroup };
